import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { formatIndianPhone } from "../../components/PhoneLink";
import { listPaymentAlerts, markPaymentAlertAsked } from "../../../services/tripTrackingApi";
import { clock, playChime, useAlarmSound } from "./SilenceAlerts";

// The server checks every minute; polling as often keeps the page at most one check behind.
const POLL_MS = 60_000;
const SOUNDED_KEY = "tripPaymentAlertsSounded";

/**
 * Day 1 trips half an hour past reaching the stay with the balance still to pay, polled while Manage
 * Bookings is open. Chimes once per alert unless an admin already clicked Asked, and says so when one
 * clears.
 */
export function usePaymentAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [askedBusy, setAskedBusy] = useState(null);
  // The alerts from the last poll that answered; null until the first, so a page load says nothing.
  const previous = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const next = await listPaymentAlerts();
      const ids = new Set(next.map((entry) => entry.id));
      (previous.current || []).forEach((entry) => {
        if (ids.has(entry.id)) return;
        toast.success(`Booking #${entry.bookingId}: payment alert cleared - the balance is paid, or the trip was completed or cancelled.`);
      });
      previous.current = next;
      setAlerts(next);
    } catch {
      // The next poll retries; what is on screen stays.
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => { void refresh(); }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [refresh]);

  // Asked silences it the way Seen silences the red alerts.
  const forSound = useMemo(() => alerts.map((entry) => ({ ...entry, seenAtIst: entry.askedAtIst })), [alerts]);
  const { soundBlocked, enableSound } = useAlarmSound(forSound, SOUNDED_KEY, playChime);

  const markAsked = useCallback(async (entry) => {
    setAskedBusy(entry.id);
    try {
      const updated = await markPaymentAlertAsked(entry.id);
      setAlerts((current) => current.map((item) => (item.id === updated.id ? updated : item)));
    } catch (error) {
      window.alert(error?.response?.data?.message || "Could not mark the payment as asked.");
    } finally {
      setAskedBusy(null);
    }
  }, []);

  return { alerts, soundBlocked, askedBusy, markAsked, enableSound };
}

const rupees = (value) => `₹${Number(value || 0).toLocaleString("en-IN")}`;

/** The green box under the red ones: each trip whose customer should now be asked for the rest. */
export function PaymentAlertPanel({ alerts, soundBlocked, askedBusy, onAsked, onEnableSound, onCopyReminder, onShowBooking }) {
  if (!alerts.length) return null;
  return (
    <div className="booking-payment-alerts" role="status">
      <div className="booking-payment-alerts-head">
        <span><i className="bi bi-cash-coin me-1" aria-hidden="true" />Day 1: ask for the remaining payment ({alerts.length})</span>
        {soundBlocked && (
          <Button size="sm" variant="light" onClick={onEnableSound}>
            <i className="bi bi-volume-up me-1" aria-hidden="true" />Turn on sound
          </Button>
        )}
      </div>
      {alerts.map((entry) => {
        const { display, dial } = formatIndianPhone(entry.customerPhone);
        return (
          <div key={entry.id} className={`booking-payment-alert${entry.askedAtIst ? " asked" : ""}`}>
            <div className="booking-silence-alert-text">
              <div className="fw-semibold">
                #{entry.bookingId} · {entry.tripTitle || "Trip"}{entry.customerName ? ` · ${entry.customerName}` : ""}
              </div>
              <div>
                Reached {entry.stayName || "the stay"} at {clock(entry.stayReachedAtIst)}
                {" · "}<strong>{rupees(entry.balanceDue)} still to pay</strong>
              </div>
              <div className="small">Customer: {entry.customerName || "-"}{display ? ` · ${display}` : ""}</div>
            </div>
            <div className="booking-silence-alert-actions">
              <Button size="sm" variant="success" onClick={() => onCopyReminder(entry.bookingId)}>
                Copy Payment Reminder
              </Button>
              {dial && (
                <Button size="sm" variant="outline-success" href={`tel:${dial}`}>
                  <i className="bi bi-telephone-fill me-1" aria-hidden="true" />Call customer
                </Button>
              )}
              <Button size="sm" variant="outline-success" onClick={() => onShowBooking(entry.bookingId)}>
                Show booking
              </Button>
              {entry.askedAtIst ? (
                <span className="booking-payment-asked">Asked by {entry.askedBy || "admin"} at {clock(entry.askedAtIst)}</span>
              ) : (
                <Button
                  size="sm"
                  variant="dark"
                  disabled={askedBusy === entry.id}
                  title="Stops the chime for every admin and shows them you asked; it stays until the payment is recorded"
                  onClick={() => onAsked(entry)}
                >
                  {askedBusy === entry.id ? <Spinner size="sm" /> : "Asked"}
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
